import type { NotificationTemplate } from '@nucleus-commerce/api-client/admin';
import { ApiError } from '@nucleus-commerce/api-client/transport';

// Same pattern the backend fills and validates: {{name}} with optional inner spaces.
const PLACEHOLDER = /\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g;

/** `store` is accepted by every event even when the list omits it. */
const ALWAYS_ALLOWED = ['store'];

export function placeholderToken(name: string): string {
  return `{{${name}}}`;
}

/** Placeholder names used in a text, in order of first appearance, without duplicates. */
export function placeholdersIn(text: string): string[] {
  return [...new Set([...text.matchAll(PLACEHOLDER)].map((match) => match[1] ?? ''))];
}

/** Names the backend would refuse with a 422, in order of first appearance. */
export function unknownPlaceholders(text: string, allowed: readonly string[]): string[] {
  const known = new Set([...allowed, ...ALWAYS_ALLOWED]);
  return placeholdersIn(text).filter((name) => !known.has(name));
}

export interface Insertion {
  value: string;
  caret: number;
}

/** Puts `{{name}}` over the selection (or at the caret) and returns where the caret lands. */
export function insertPlaceholder(
  value: string,
  name: string,
  selectionStart: number | null,
  selectionEnd: number | null,
): Insertion {
  const start = Math.min(Math.max(selectionStart ?? value.length, 0), value.length);
  const end = Math.min(Math.max(selectionEnd ?? start, start), value.length);
  const token = placeholderToken(name);
  return { value: value.slice(0, start) + token + value.slice(end), caret: start + token.length };
}

/** A wording override exists; an override that only switches the email off does not count. */
export function isCustomised(template: Pick<NotificationTemplate, 'override'>): boolean {
  const override = template.override;
  return Boolean(override?.subject ?? override?.body);
}

/** Blank means "use the default", which the API expresses as null. */
export function toOverrideText(value: string): string | null {
  return value.trim() === '' ? null : value;
}

/** The unknown names of a 422 from save or preview (`meta.unknown`), if that is what it is. */
export function unknownFromError(error: unknown): string[] | null {
  if (!(error instanceof ApiError) || error.kind !== 'validation') return null;
  const unknown = error.meta.unknown;
  return Array.isArray(unknown)
    ? unknown.filter((name): name is string => typeof name === 'string')
    : null;
}
